import * as React from "react";
import { useEffect, useRef, useState } from "react";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import { Box, Grid } from "@mui/material";
import { useParams } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BoltIcon from "@mui/icons-material/Bolt";
import WifiIcon from "@mui/icons-material/Wifi";
import {
  useEditJobCardMutation,
  useEditIMSSmartnodeMutation,
  useGetJobCardByReferenceQuery,
  useGetSmartNodeStatusQuery,
} from "../api/apiSlice";
import { cardDevice, deviceStatusLabel } from "./deviceCard";
import { useConnectionMonitor } from "./connectionsTest";
import ConnectionAlert from "./ConnectionAlert";
import NavbarStepper from "./Stepper/NavBarStepper";
import AbortJobButton from "./AbortJobButton";
import { AMBER, LABEL, CARD_BG, BORDER, SUCCESS } from "../../Global/colors";
import Drawer from "@mui/material/Drawer";
import { useClock } from "./useClock";
import { useDispatch } from "react-redux";
import { showError } from "../../Global/notifications/notificationsSlice";

// ─── Shared card style ─────────────────────────────────────────────────────────

const cardSx = {
  backgroundColor: CARD_BG,
  border: `1px solid ${BORDER}`,
  borderRadius: "12px",
  p: "14px 16px",
  mb: "10px",
};

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function JobCompletePage() {
  const {
    showAlert,
    showWeakWarning,
    handleRetry,
    handleDismiss,
    handleWeakWarningDismiss,
    responseTime,
    avgResponseTime,
    connectionDetails,
  } = useConnectionMonitor();

  const navigate = useNavigate();
  const { job_card_reference_id } = useParams();
  const { data: job_card_data, isSuccess: isGetJobCardStateSuccess } =
    useGetJobCardByReferenceQuery(job_card_reference_id);

  const [showSuccess, setShowSuccess] = useState(false);
  const [canComplete, setCanComplete] = useState(false);
  const sentinelRef = useRef(null);

  // Unlock "Complete Job" once the user scrolls past the sentinel at the bottom
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !isGetJobCardStateSuccess) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setCanComplete(true); },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [isGetJobCardStateSuccess]);

  const dispatch = useDispatch();
  const [editIMSSmartNode, { isLoading: isUpdatingIMS }] = useEditIMSSmartnodeMutation();
  const [editJobCard, { isLoading: isUpdatingJobCard }] = useEditJobCardMutation();
  const submittingRef = useRef(false);

  const isCompleting = isUpdatingIMS || isUpdatingJobCard;

  // Already with QE (or approved): the job is done here. A reopened or remounted page used
  // to show the review again, and its button kept writing "Uninstall complete" to IMS on
  // every tap while the job itself never moved (stress test 22/10, T9).
  const alreadySubmitted = ["QEP", "COM"].includes(job_card_data?.state);

  // The job card is submitted first; IMS is told once, only after that succeeds. It used to
  // be the other way round, chained through the IMS mutation's isSuccess, which stays true
  // after the first call - so repeat taps wrote IMS each time and never resubmitted.
  const handleCompleteJob = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await editJobCard({
        body: { reference_id: job_card_reference_id, state: "QEP", qe_job_state: "Pending" },
      }).unwrap();
    } catch (error) {
      if (error?.data?.already_submitted) {
        // Submitted from an earlier try; IMS was told then.
        setShowSuccess(true);
        return;
      }
      submittingRef.current = false;
      dispatch(showError(error?.data?.error || "Could not submit the job. Check your signal and try again."));
      return;
    }
    try {
      await updateIMS();
    } catch (error) {
      dispatch(showError("Submitted for QE review, but the stock record was not updated. Let your Field Admin know."));
    }
    setShowSuccess(true);
  };

  const updateIMS = () => {
    if (
      job_card_data.install_state === "UN" ||
      job_card_data.install_state === "RE"
    ) {
      return editIMSSmartNode({
        body: {
          serial_number: job_card_data.archived_serial_number,
          imsi_number: job_card_data.archived_imsi_number,
          status: "Pending",
          change:
            job_card_data.install_state === "UN"
              // Not "collected": the device is Pending until QE approves (P4).
              ? "Uninstall submitted, waiting for QE to approve"
              : "Replacement submitted, waiting for QE to approve",
          // The device coming off may belong to another company's stock; IMS skips its
          // company check for an uninstall's updates when this is set.
          uninstall: true,
        },
      }).unwrap();
    } else {
      return editIMSSmartNode({
        body: {
          serial_number: job_card_data.serial_number,
          imsi_number: job_card_data.imsi_number,
          status: "Pending",
          change: "Installed in field, waiting for QE to approve",
        },
      }).unwrap();
    }
  };

  const { handleClockOut } = useClock();

  if (showSuccess || alreadySubmitted) {
    return (
      <SuccessScreen
        referenceId={job_card_reference_id}
        installState={job_card_data?.install_state}
        onDone={() => navigate("/installer/", { replace: true })}
        onClockOut={() => { handleClockOut(); navigate("/installer/", { replace: true }); }}
      />
    );
  }

  return (
    <Box sx={{ bgcolor: "#0D0E14", minHeight: "100vh" }}>
      <NavbarStepper complete />

      {showAlert && (
        <ConnectionAlert
          onRetry={handleRetry}
          onClose={handleDismiss}
          connectionDetails={connectionDetails}
          position="top"
          alertType="error"
        />
      )}
      {showWeakWarning && (
        <ConnectionAlert
          onRetry={handleRetry}
          onClose={handleWeakWarningDismiss}
          connectionDetails={connectionDetails}
          position="top"
          alertType="warning"
          responseTime={responseTime || avgResponseTime}
        />
      )}

      {/* Scrollable content */}
      <Box sx={{ px: "16px", pt: "16px", pb: "100px", maxWidth: "480px", mx: "auto" }}>
        {/* Page heading */}
        <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, color: "#fff", mb: "4px" }}>
          Review everything before completing
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: LABEL, mb: "18px" }}>
          Scroll through each section. The Complete button unlocks once you reach the bottom.
        </Typography>

        {isGetJobCardStateSuccess && (
          <>
            <InstallationDetails data={job_card_data} />
            <PhotoEvidence data={job_card_data} />
            <Measurements data={job_card_data} />
            <DeviceInfo data={job_card_data} />
          </>
        )}

        {/* Sentinel — Complete button unlocks when this is visible */}
        <Box ref={sentinelRef} sx={{ height: 1 }} />
      </Box>

      {/* Fixed bottom bar */}
      <Box sx={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        display: "flex", gap: "12px",
        px: "16px", py: "14px",
        background: "rgba(13,14,20,0.95)",
        backdropFilter: "blur(12px)",
        borderTop: `1px solid ${BORDER}`,
      }}>
        <AbortJobButton />
        <Button
          variant="contained"
          disableElevation
          disabled={!canComplete || !isGetJobCardStateSuccess || isCompleting}
          onClick={handleCompleteJob}
          sx={{
            flex: 1,
            backgroundColor: AMBER,
            color: "#000",
            fontWeight: 700,
            fontSize: "1rem",
            py: "13px",
            borderRadius: "10px",
            textTransform: "none",
            border: "none",
            opacity: (!canComplete || !isGetJobCardStateSuccess || isCompleting) ? 0.45 : 1,
            "&:hover": { backgroundColor: "#e6a832" },
            "&.Mui-disabled": { opacity: 0.45 },
          }}
        >
          {isCompleting ? "Submitting..." : completeLabel(job_card_data?.install_state)}
        </Button>
      </Box>
    </Box>
  );
}

function completeLabel(installState) {
  if (installState === "UN") return "Complete Uninstall";
  if (installState === "RE") return "Complete Replacement";
  return "Complete Job";
}

// ─── Section card wrapper ──────────────────────────────────────────────────────

function SectionCard({ icon, title, badge, badgeColor, children }) {
  return (
    <Box sx={cardSx}>
      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", mb: "12px" }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: "8px",
          backgroundColor: "rgba(253,185,64,0.13)",
          display: "flex", alignItems: "center", justifyContent: "center",
          mr: "10px", flexShrink: 0,
        }}>
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#fff", flex: 1 }}>
          {title}
        </Typography>
        {badge != null && (
          <Typography sx={{
            fontSize: "0.75rem", fontWeight: 700,
            color: badgeColor || AMBER,
            ml: "8px",
          }}>
            {badge}
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}

// ─── Detail row ────────────────────────────────────────────────────────────────

function DetailRow({ label, value, valueColor, last }) {
  return (
    <Box sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      py: "9px",
      gap: "16px",
      borderBottom: last ? "none" : `1px solid ${BORDER}`,
    }}>
      <Typography sx={{ color: LABEL, fontSize: "0.83rem", flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography sx={{
        color: valueColor || "#fff",
        fontWeight: 600,
        fontSize: "0.83rem",
        textAlign: "right",
      }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

// ─── Installation details ──────────────────────────────────────────────────────

function InstallationDetails({ data }) {
  const activeFlags = [
    data.breaker_control    && "Breaker control",
    data.solar_geyser       && "Solar geyser",
    data.solar_pv           && "Solar PV",
    data.timer_installed    && "Timer installed",
    data.ripple_replacement && "Ripple replacement",
    data.heat_pump          && "Heat pump",
    data.ripple_bypass      && "Ripple bypass",
  ].filter(Boolean);

  const consentColor =
    data.customer_consent === "Willing" ? SUCCESS :
    data.customer_consent === "Unwilling" ? "#ef5350" : LABEL;

  const propertyLabel = data.property_type === "complex" ? "Complex" : "House";

  return (
    <SectionCard
      icon={<DescriptionOutlinedIcon sx={{ color: AMBER, fontSize: "1rem" }} />}
      title="Installation details"
    >
      <DetailRow label="Address" value={data.address} />
      {data.property_type === "complex" && data.building_name && (
        <DetailRow label="Building" value={data.building_name} />
      )}
      {data.property_type === "complex" && data.unit_number && (
        <DetailRow label="Unit" value={data.unit_number} />
      )}
      <DetailRow label="Property type" value={propertyLabel} />
      {/* Description and Erf are saved and QE sees them, but the installer couldn't check them
          here before submitting; the Geyser row read a field no step captures, so it was
          always "—" (stress test 26/10, P3). */}
      <DetailRow label="Description" value={data.address_description} />
      <DetailRow label="Erf" value={data.erf_number} />
      {data.install_state !== "UN" && (
        <DetailRow
          label="Customer consent"
          value={data.customer_consent}
          valueColor={consentColor}
        />
      )}
      {activeFlags.length > 0 && (
        <DetailRow
          label="Flags"
          value={activeFlags.join(" · ")}
          last
        />
      )}
      {activeFlags.length === 0 && (
        <DetailRow label="Flags" value="None" valueColor={LABEL} last />
      )}
    </SectionCard>
  );
}

// ─── Photo evidence ────────────────────────────────────────────────────────────

function PhotoEvidence({ data }) {
  const isNew = data.install_state === "NEW";

  const photos = isNew
    ? [
        { key: "clamp_meter_before_photo", label: "Clamp · pre" },
        { key: "install_photo_before",     label: "Work area · before" },
        { key: "before_photo_on",          label: "DB cover-on · before" },
        { key: "before_photo_off",         label: "DB cover-off · before" },
        { key: "clamp_meter_photo",        label: "Clamp · post" },
        { key: "install_photo_after",      label: "Work area · after" },
        { key: "after_photo_off",          label: "DB cover-off · after" },
        { key: "after_photo_on",           label: "DB cover-on · after" },
      ]
    : [
        { key: "install_photo_before",  label: "Work area · before" },
        { key: "before_photo_on",       label: "DB cover-on · before" },
        { key: "before_photo_off",      label: "DB cover-off · before" },
        { key: "install_photo_after",   label: "Work area · after" },
        { key: "after_photo_off",       label: "DB cover-off · after" },
        { key: "after_photo_on",        label: "DB cover-on · after" },
      ];

  const captured = photos.filter(({ key }) => data[key]).length;
  const total = photos.length;
  const allCaptured = captured === total;

  return (
    <SectionCard
      icon={<CameraAltOutlinedIcon sx={{ color: AMBER, fontSize: "1rem" }} />}
      title="Photo evidence"
      badge={`${captured} / ${total}`}
      badgeColor={allCaptured ? SUCCESS : AMBER}
    >
      <Box sx={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: "6px",
      }}>
        {photos.map(({ key, label }) => (
          <Box key={key}>
            <Box sx={{
              aspectRatio: "1",
              borderRadius: "6px",
              overflow: "hidden",
              backgroundColor: "#0d1117",
              border: data[key]
                ? "1px solid rgba(34,197,94,0.35)"
                : `1px dashed rgba(255,255,255,0.08)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              {data[key] ? (
                <img
                  src={data[key]}
                  alt={label}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <CameraAltOutlinedIcon sx={{ color: AMBER, opacity: 0.25, fontSize: "0.9rem" }} />
              )}
            </Box>
            <Typography sx={{
              fontSize: "0.52rem",
              color: LABEL,
              textAlign: "center",
              mt: "3px",
              lineHeight: 1.2,
            }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
}

// ─── Measurements ──────────────────────────────────────────────────────────────

function Measurements({ data }) {
  const hasAmpere = data.ampere != null;
  const hasNotes = data.installer_notes?.trim();
  // Shown separately because they are written at a different step. On an uninstall the
  // removal notes are the only notes the card has, so without this the card reads as
  // though the installer wrote nothing.
  const hasUninstallNotes = data.uninstall_notes?.trim();

  if (!hasAmpere && !hasNotes && !hasUninstallNotes) return null;

  return (
    <SectionCard
      icon={<BoltIcon sx={{ color: AMBER, fontSize: "1rem" }} />}
      title="Measurements"
    >
      {hasAmpere && (
        <DetailRow label="Amp reading" value={`${data.ampere} A`} />
      )}
      {hasNotes && (
        <Box sx={{ pt: "8px", borderTop: hasAmpere ? `1px solid ${BORDER}` : "none" }}>
          <Typography sx={{ color: LABEL, fontSize: "0.72rem", mb: "6px" }}>
            Installer notes
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.83rem", lineHeight: 1.5 }}>
            {data.installer_notes}
          </Typography>
        </Box>
      )}
      {hasUninstallNotes && (
        <Box sx={{ pt: "8px", borderTop: (hasAmpere || hasNotes) ? `1px solid ${BORDER}` : "none" }}>
          <Typography sx={{ color: LABEL, fontSize: "0.72rem", mb: "6px" }}>
            Uninstall notes
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.83rem", lineHeight: 1.5 }}>
            {data.uninstall_notes}
          </Typography>
        </Box>
      )}
    </SectionCard>
  );
}

// ─── Device info ───────────────────────────────────────────────────────────────

function DeviceInfo({ data }) {
  // The device's own status, not the phone's connection (NEU-1119). An uninstall shows the
  // removed unit, which has nothing to report.
  const { serial, imsi, checkStatus } = cardDevice(data);
  const { data: status, isLoading, isError } = useGetSmartNodeStatusQuery(
    { serial_number: serial },
    { skip: !checkStatus },
  );
  const { text: statusText, tone } = deviceStatusLabel({ isLoading, isError, connected: !!status?.connected });
  const statusColor = tone === "ok" ? SUCCESS : tone === "bad" ? "#ef5350" : LABEL;

  return (
    <SectionCard
      icon={<WifiIcon sx={{ color: AMBER, fontSize: "1rem" }} />}
      title={data.install_state === "UN" ? "Removed device" : "Device"}
    >
      {checkStatus && <DetailRow label="Status" value={statusText} valueColor={statusColor} />}
      <DetailRow label="Serial" value={serial ? `SN  ${serial}` : "—"} />
      <DetailRow label="IMSI" value={imsi} last />
    </SectionCard>
  );
}

// ─── Success screen ────────────────────────────────────────────────────────────

function SuccessScreen({ referenceId, installState, onDone, onClockOut }) {
  const [clockOutOpen, setClockOutOpen] = useState(false);

  const heading =
    installState === "UN"
      ? "Uninstall Complete"
      : installState === "RE"
      ? "Replacement Complete"
      : "Installation Complete";

  return (
    <Box sx={{
      bgcolor: "#0D0E14",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      px: "24px",
    }}>
      <Box sx={{
        width: 72, height: 72, borderRadius: "50%",
        backgroundColor: "rgba(253,185,64,0.15)",
        display: "flex", alignItems: "center", justifyContent: "center",
        mb: "24px",
      }}>
        <CheckCircleOutlineIcon sx={{ color: AMBER, fontSize: 40 }} />
      </Box>

      <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#fff", mb: "6px" }}>
        {heading}
      </Typography>
      <Typography sx={{ fontSize: "0.85rem", color: LABEL, mb: "28px" }}>
        Submitted for QE review
      </Typography>

      <Box sx={{
        backgroundColor: "#161B22",
        border: `1px solid ${BORDER}`,
        borderRadius: "12px",
        px: "24px", py: "16px",
        display: "flex", alignItems: "center", gap: "16px",
        mb: "32px", alignSelf: "stretch",
      }}>
        <Typography sx={{ fontSize: "0.75rem", color: LABEL, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          REF
        </Typography>
        <Typography sx={{ color: AMBER, fontSize: "1rem", fontWeight: 700, fontFamily: "monospace", letterSpacing: "0.04em" }}>
          {referenceId}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: "12px", alignSelf: "stretch" }}>
        <Button
          fullWidth
          onClick={onDone}
          disableElevation
          sx={{
            backgroundColor: "rgba(255,255,255,0.07)", color: "#fff",
            fontWeight: 700, fontSize: "0.95rem", py: "14px",
            borderRadius: "12px", textTransform: "none",
            border: "1px solid rgba(255,255,255,0.1)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.11)" },
          }}
        >
          Back to dashboard
        </Button>
        <Button
          fullWidth
          onClick={() => setClockOutOpen(true)}
          disableElevation
          sx={{
            backgroundColor: AMBER, color: "#000", fontWeight: 700,
            fontSize: "0.95rem", py: "14px", borderRadius: "12px",
            textTransform: "none",
            "&:hover": { backgroundColor: "#e6a832" },
          }}
        >
          Done for the day
        </Button>
      </Box>

      {/* Clock-out confirmation drawer */}
      <Drawer
        anchor="bottom"
        open={clockOutOpen}
        onClose={() => setClockOutOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#12131A",
            borderRadius: "20px 20px 0 0",
            px: "20px", pt: "12px", pb: "32px",
          },
        }}
      >
        <Box sx={{
          width: "40px", height: "4px", borderRadius: "2px",
          backgroundColor: "rgba(255,255,255,0.15)",
          mx: "auto", mb: "20px",
        }} />
        <Typography sx={{ color: AMBER, fontWeight: 700, fontSize: "1.1rem", mb: "8px" }}>
          Clock out for the day?
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.88rem", lineHeight: 1.55, mb: "24px" }}>
          Your shift will be saved with today's stats.
        </Typography>
        <Button
          fullWidth
          disableElevation
          onClick={onClockOut}
          sx={{
            backgroundColor: AMBER, color: "#000", fontWeight: 700,
            fontSize: "1rem", py: "14px", borderRadius: "12px",
            textTransform: "none", mb: "10px",
            "&:hover": { backgroundColor: "#e6a832" },
          }}
        >
          Yes — clock out
        </Button>
        <Button
          fullWidth
          disableElevation
          onClick={() => { setClockOutOpen(false); onDone(); }}
          sx={{
            backgroundColor: "rgba(255,255,255,0.07)", color: "#fff",
            fontWeight: 700, fontSize: "1rem", py: "14px",
            borderRadius: "12px", textTransform: "none",
            border: "1px solid rgba(255,255,255,0.1)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.11)" },
          }}
        >
          Keep shift running
        </Button>
      </Drawer>
    </Box>
  );
}
